/**
 * OfflineIndicator — small fixed-position chip that surfaces the
 * navigator.onLine state. When offline, shows a slate-900 chip in
 * the bottom-left corner so the user knows why mutations are failing
 * and reads (warranty PDF, recent customers, help center) are coming
 * from the service-worker cache.
 *
 * Wired once at the app level so it appears on every page.
 */
import { useEffect, useState } from "react";
import { CloudSlash } from "@phosphor-icons/react";
import { useT } from "@/lib/i18n";

export default function OfflineIndicator() {
  const t = useT();
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine !== false : true
  );

  useEffect(() => {
    const onUp = () => setOnline(true);
    const onDown = () => setOnline(false);
    window.addEventListener("online", onUp);
    window.addEventListener("offline", onDown);
    return () => {
      window.removeEventListener("online", onUp);
      window.removeEventListener("offline", onDown);
    };
  }, []);

  if (online) return null;

  return (
    <div
      data-testid="offline-indicator"
      className="fixed bottom-4 left-4 z-[55] flex items-center gap-2 px-3 py-2 rounded-sm bg-slate-900 text-white shadow-2xl border border-slate-700 text-xs font-bold uppercase tracking-wider"
      role="status"
      aria-live="polite"
    >
      <CloudSlash size={14} weight="fill" className="text-amber-400" />
      <span>{t("offline.label", "Offline")}</span>
      <span className="hidden sm:inline text-[10px] font-normal text-slate-400 normal-case">
        {t("offline.hint", "Showing cached data")}
      </span>
    </div>
  );
}
